import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, UserPlus, UserCheck } from 'lucide-react';
import { API, authFetch } from '@/App';
import { toast } from 'sonner';
import GlowBadge from '@/components/GlowBadge';

export default function GlowCreatorsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchCreators(); }, []);

  // Re-fetch when navigating back to this page
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchCreators();
  }, [location.key]);

  const fetchCreators = async () => {
    try {
      const response = await authFetch(`${API}/glow-creators`);
      if (response.ok) setCreators(await response.json());
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async (userId) => {
    try {
      const response = await authFetch(`${API}/users/${userId}/follow`, { method: 'POST' });
      if (response.ok) {
        const data = await response.json();
        const isRequested = data.status === 'requested';
        setCreators(prev => prev.map(c =>
          c.user_id === userId ? { ...c, is_following: !isRequested, is_requested: isRequested } : c
        ));
        toast.success(isRequested ? 'Follow request sent!' : 'Following!');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24">
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <button data-testid="back-btn" onClick={() => navigate(-1)} className="p-2 hover:bg-primary/10 rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Glow Creators</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4">
        {creators.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-lg font-medium text-foreground mb-2">No Glow Creators yet</p>
            <p className="text-sm text-muted-foreground">Verified creators will appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {creators.map(creator => (
              <div
                key={creator.user_id}
                data-testid={`creator-${creator.user_id}`}
                className="flex items-center gap-3 p-4 rounded-2xl bg-card border border-border hover:border-primary/30 transition-colors"
              >
                <button onClick={() => navigate(`/profile/${creator.user_id}`)} className="shrink-0">
                  {creator.picture ? (
                    <img src={creator.picture} alt={creator.name} className="w-14 h-14 rounded-full object-cover" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold text-lg">
                      {creator.name?.charAt(0).toUpperCase()}
                    </div>
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <button onClick={() => navigate(`/profile/${creator.user_id}`)} className="text-left">
                    <div className="flex items-center gap-1.5">
                      <span className="font-bold text-foreground truncate">{creator.name}</span>
                      <GlowBadge tier={creator.glow_badge} />
                    </div>
                    <p className="text-sm text-muted-foreground">@{creator.username}</p>
                  </button>
                  {creator.bio && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{creator.bio}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-0.5">{creator.followers_count} followers</p>
                </div>
                {!creator.is_following && !creator.is_requested ? (
                  <button
                    data-testid={`follow-${creator.user_id}`}
                    onClick={() => handleFollow(creator.user_id)}
                    className="btn-primary px-4 py-2 text-sm flex items-center gap-1.5 shrink-0"
                  >
                    <UserPlus className="w-4 h-4" />
                    Follow
                  </button>
                ) : creator.is_requested ? (
                  <span className="text-xs text-muted-foreground flex items-center gap-1 px-3 py-2 rounded-xl bg-muted shrink-0">
                    Requested
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground flex items-center gap-1 px-3 py-2 rounded-xl bg-muted shrink-0">
                    <UserCheck className="w-4 h-4" />
                    Following
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
